/**
 * Utility class for managing dropdown positioning and viewport adjustments.
 * Handles scroll and resize events to keep dropdown visible and properly positioned.
 */
export class DropdownPositioner {
  private scrollListener: (() => void) | null = null;
  private resizeListener: (() => void) | null = null;

  /**
   * Creates a new DropdownPositioner instance.
   *
   * @param dropdown - The dropdown menu element to position
   * @param trigger - The trigger element that opens the dropdown
   * @param onClose - Callback function to close the dropdown when scrolled out of view
   */
  constructor(
    private readonly dropdown: HTMLElement,
    private readonly trigger: HTMLElement,
    private readonly onClose: () => void,
  ) {}

  /**
   * Adjusts dropdown position and scrolls viewport if needed to ensure dropdown is fully visible.
   * Determines whether to position dropdown above or below the trigger based on available space.
   */
  adjust(): void {
    // Reset inline overrides so the dropdown is in its natural CSS position (below trigger)
    this.dropdown.style.top = '';
    this.dropdown.style.bottom = '';
    this.dropdown.style.maxHeight = '';
    this.dropdown.style.overflowY = '';

    const triggerRect = this.trigger.getBoundingClientRect();
    const dropdownRect = this.dropdown.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const GAP = 4;
    const EDGE_PADDING = 20;

    const spaceBelow = viewportHeight - triggerRect.bottom - GAP - EDGE_PADDING;
    const spaceAbove = triggerRect.top - GAP - EDGE_PADDING;
    const dropdownHeight = dropdownRect.height;

    let openUpward = false;

    if (dropdownHeight > spaceBelow) {
      if (spaceAbove > spaceBelow && spaceAbove >= dropdownHeight) {
        // More space above and enough room — flip the dropdown upward
        this.dropdown.style.top = 'auto';
        this.dropdown.style.bottom = 'calc(100% + 4px)';
        openUpward = true;
      } else {
        // Keep below but scroll viewport so the dropdown is visible
        const scrollAmount =
          triggerRect.bottom + dropdownHeight + GAP + EDGE_PADDING - viewportHeight;
        window.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      }
    }

    // Constrain max-height based on available space in the direction the dropdown opens
    const availableSpace = openUpward ? spaceAbove : spaceBelow;
    const maxHeight = Math.min(availableSpace, 600);

    if (maxHeight > 0 && maxHeight < dropdownHeight) {
      this.dropdown.style.maxHeight = `${maxHeight}px`;
      this.dropdown.style.overflowY = 'auto';
    }
  }

  /**
   * Starts listening to scroll and resize events.
   * Should be called when dropdown is opened.
   */
  startListening(): void {
    this.addScrollListener();
    this.addResizeListener();
  }

  /**
   * Stops listening to scroll and resize events.
   * Should be called when dropdown is closed or component is destroyed.
   */
  stopListening(): void {
    this.removeScrollListener();
    this.removeResizeListener();
  }

  /**
   * Adds scroll listener to check dropdown visibility on scroll.
   */
  private addScrollListener(): void {
    this.removeScrollListener();
    this.scrollListener = () => {
      this.checkVisibility();
    };
    window.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  /**
   * Removes scroll listener.
   */
  private removeScrollListener(): void {
    if (this.scrollListener !== null) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }

  /**
   * Adds resize listener to adjust dropdown position on window resize.
   */
  private addResizeListener(): void {
    this.removeResizeListener();
    this.resizeListener = () => {
      this.adjust();
    };
    window.addEventListener('resize', this.resizeListener, { passive: true });
  }

  /**
   * Removes resize listener.
   */
  private removeResizeListener(): void {
    if (this.resizeListener !== null) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
  }

  /**
   * Checks if dropdown is still visible in viewport.
   * Closes dropdown if trigger is scrolled out of view.
   */
  private checkVisibility(): void {
    const triggerRect = this.trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Close dropdown if trigger is scrolled out of viewport
    if (triggerRect.bottom < 0 || triggerRect.top > viewportHeight) {
      this.onClose();
    }
  }
}
